import { useState } from "react";
import { Wallet, Loader2, Unplug } from "lucide-react";
import { Panel, PanelHeader, Label, Chip } from "./primitives";
import { DataStatus } from "./DataStatus";
import { apiGet, timeAgo } from "../lib/api";

const CHAIN_ID_HEX = "0x1237";
const PUBLIC_RPC = process.env.REACT_APP_PUBLIC_RPC;

export const WalletPanel = () => {
  const [address, setAddress] = useState("");
  const [input, setInput] = useState("");
  const [data, setData] = useState(null);
  const [status, setStatus] = useState("idle"); // idle | loading | ok | error
  const [err, setErr] = useState("");

  const load = async (addr) => {
    setStatus("loading");
    setErr("");
    try {
      const d = await apiGet(`/wallet/${addr}`);
      setData(d);
      setStatus("ok");
    } catch (e) {
      setStatus("error");
      setErr(e?.payload?.error === "INVALID_ADDRESS" ? "Invalid wallet address" : "RPC CONNECTION ERROR");
    }
  };

  const connectInjected = async () => {
    if (!window.ethereum) {
      setStatus("error");
      setErr("No EVM wallet detected in this browser");
      return;
    }
    try {
      const accs = await window.ethereum.request({ method: "eth_requestAccounts" });
      try {
        await window.ethereum.request({ method: "wallet_switchEthereumChain", params: [{ chainId: CHAIN_ID_HEX }] });
      } catch (sw) {
        if (sw?.code === 4902) {
          await window.ethereum.request({
            method: "wallet_addEthereumChain",
            params: [{
              chainId: CHAIN_ID_HEX,
              chainName: "Robinhood Chain",
              nativeCurrency: { name: "Ether", symbol: "ETH", decimals: 18 },
              rpcUrls: [PUBLIC_RPC],
            }],
          });
        }
      }
      setAddress(accs[0]);
      load(accs[0]);
    } catch {
      setStatus("idle");
    }
  };

  const track = () => {
    const a = input.trim();
    if (!/^0x[0-9a-fA-F]{40}$/.test(a)) {
      setStatus("error");
      setErr("Invalid wallet address");
      return;
    }
    setAddress(a);
    load(a);
  };

  const disconnect = () => {
    setAddress("");
    setData(null);
    setStatus("idle");
    setInput("");
    setErr("");
  };

  return (
    <Panel data-testid="wallet-panel">
      <PanelHeader right={status === "ok" ? <DataStatus status="LIVE" label="Robinhood Chain RPC" /> : <Chip tone="muted">Read-Only</Chip>}>
        Wallet — Real Balances
      </PanelHeader>

      {status !== "ok" ? (
        <div className="p-4">
          <p className="mb-4 text-xs leading-relaxed text-fog-400">
            Connect an EVM wallet or paste an address to read real onchain balances directly from Robinhood Chain.
            No simulated balances are displayed.
          </p>
          <button
            onClick={connectInjected}
            data-testid="wallet-connect"
            className="flex w-full items-center justify-center gap-2 border border-pulse/30 bg-pulse/10 py-2.5 font-mono text-[11px] uppercase tracking-[0.18em] text-pulse-soft transition-all hover:bg-pulse/20"
          >
            <Wallet className="h-3.5 w-3.5" /> Connect Wallet
          </button>
          <div className="my-3 flex items-center gap-3">
            <span className="h-px flex-1 bg-white/[0.08]" />
            <span className="font-mono text-[9px] uppercase tracking-[0.2em] text-fog-600">or track address</span>
            <span className="h-px flex-1 bg-white/[0.08]" />
          </div>
          <div className="flex gap-2">
            <input
              value={input}
              onChange={(e) => setInput(e.target.value)}
              placeholder="0x…"
              data-testid="wallet-address-input"
              className="w-full border border-white/10 bg-ink-900 px-3 py-2.5 font-mono text-xs text-fog-100 placeholder:text-fog-600 focus:border-pulse/50 focus:outline-none"
            />
            <button
              onClick={track}
              data-testid="wallet-track"
              className="shrink-0 border border-white/15 px-4 font-mono text-[10px] uppercase tracking-[0.18em] text-fog-100 transition-colors hover:border-pulse/50 hover:text-pulse-soft"
            >
              {status === "loading" ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : "Load"}
            </button>
          </div>
          {status === "error" && (
            <div className="mt-3 border border-red-400/30 bg-red-400/5 px-3 py-2 font-mono text-[10px] uppercase tracking-[0.18em] text-red-400" data-testid="wallet-error">
              {err}
            </div>
          )}
        </div>
      ) : (
        <div className="p-4">
          <div className="mb-4 flex items-center justify-between">
            <span className="font-mono text-xs text-fog-100" data-testid="wallet-address">
              {address.slice(0, 8)}…{address.slice(-6)}
            </span>
            <button onClick={disconnect} data-testid="wallet-disconnect"
              className="flex items-center gap-1.5 font-mono text-[10px] uppercase tracking-[0.15em] text-fog-600 hover:text-fog-100">
              <Unplug className="h-3 w-3" /> Disconnect
            </button>
          </div>

          <div className="mb-4 flex items-baseline justify-between border border-white/[0.08] bg-ink-900 px-4 py-3">
            <Label>Native Balance</Label>
            <span className="font-mono text-xl font-bold text-fog-100" data-testid="wallet-eth-balance">
              {data.nativeBalanceFormatted} <span className="text-xs text-fog-400">{data.nativeSymbol}</span>
            </span>
          </div>

          <Label className="mb-2">Stock Token Balances — {data.tokensScanned} Contracts Scanned</Label>
          {data.tokens.length === 0 ? (
            <div className="border border-white/[0.06] px-3 py-3 font-mono text-xs text-fog-600" data-testid="wallet-no-tokens">
              0 — no stock token balances on this address
            </div>
          ) : (
            <div className="max-h-56 overflow-y-auto border border-white/[0.06]" data-testid="wallet-tokens">
              {data.tokens.map((t) => (
                <div key={t.contractAddress} className="flex items-center gap-3 border-b border-white/[0.04] px-3 py-2 last:border-0">
                  {t.logoUrl && <img src={t.logoUrl} alt="" className="h-5 w-5 rounded-full" loading="lazy" />}
                  <div className="min-w-0 flex-1">
                    <span className="font-mono text-xs font-bold text-fog-100">{t.symbol}</span>
                    <span className="block truncate font-mono text-[9px] text-fog-600">{t.contractAddress}</span>
                  </div>
                  <div className="text-right">
                    <span className="font-mono text-xs text-fog-100">{t.balanceFormatted}</span>
                    <span className="block font-mono text-[9px] text-fog-600">USD N/A</span>
                  </div>
                </div>
              ))}
            </div>
          )}

          <div className="mt-3 flex items-center justify-between font-mono text-[9px] uppercase tracking-[0.18em] text-fog-600">
            <span>Source · {data.source}</span>
            <span>Updated · {timeAgo(data.timestamp)}</span>
          </div>
        </div>
      )}
    </Panel>
  );
};
